// @ts-strict
import {type FormikErrors} from 'formik';

import {CATEGORY_LEVEL_1, CUSTOMER_CASCADE} from './customerCascade';
import {
  type FieldContext,
  type FieldId,
  StepEnum,
  SUPPORT_MESSAGE_SOURCES,
  type SupportCaseInput,
  type SupportMessageSource,
  type SupportMessageSurface,
  type WebToCaseFormValues,
} from './types';
import {webToCaseFields} from './webToCaseFields';

type Translate = (key: string) => string;
type Option = {value: string; label: string};

export const getCategoryLevel1Options = (t: Translate): Option[] =>
  CATEGORY_LEVEL_1.map(({value, labelKey}) => ({value, label: t(labelKey)}));

export const getCategoryLevel2Options = (
  categoryLevel1: WebToCaseFormValues['categoryLevel1'],
  t: Translate,
): Option[] => {
  if (categoryLevel1 === '') return [];

  return CUSTOMER_CASCADE[categoryLevel1].map(({value, labelKey}) => ({
    value,
    label: t(labelKey),
  }));
};

export const getCategoryLevel2Label = (
  categoryLevel1: WebToCaseFormValues['categoryLevel1'],
  t: Translate,
): string => {
  const entry = CATEGORY_LEVEL_1.find(option => option.value === categoryLevel1);

  return t(entry?.categoryLevel2LabelKey ?? 'components.SupportMessage.fields.categoryLevel2.none');
};

export const isFieldVisible = (id: FieldId, values: WebToCaseFormValues): boolean => {
  const field = webToCaseFields.find(f => f.id === id);
  if (!field?.visibleWhen) return true;

  return field.visibleWhen(values);
};

export const getActiveCategoryLevel2 = (values: WebToCaseFormValues): string => {
  if (values.categoryLevel1 === '') return '';

  return values.categoryLevel2ByCategory1[values.categoryLevel1] ?? '';
};

export const categoryLevel2Path = (categoryLevel1: WebToCaseFormValues['categoryLevel1']): string =>
  `categoryLevel2ByCategory1.${categoryLevel1 || 'unset'}`;

export const validateSupportMessage = (
  values: WebToCaseFormValues,
  t: Translate,
): FormikErrors<WebToCaseFormValues> => {
  const errors: FormikErrors<WebToCaseFormValues> = {};

  webToCaseFields.forEach(field => {
    if (!isFieldVisible(field.id, values)) return;

    if (field.id === 'categoryLevel2') {
      if (
        field.required &&
        values.categoryLevel1 !== '' &&
        getActiveCategoryLevel2(values) === ''
      ) {
        errors.categoryLevel2ByCategory1 = {
          ...errors.categoryLevel2ByCategory1,
          [values.categoryLevel1]: t('components.SupportMessage.errors.required'),
        };
      }
      return;
    }

    const key = field.id;
    const value = values[key];

    if (value === '') {
      if (field.required) errors[key] = t('components.SupportMessage.errors.required');
      return;
    }

    if (field.format && !field.format.pattern.test(value)) {
      errors[key] = t(field.format.errorKey);
    }
  });

  return errors;
};

export const buildSupportCaseInput = (
  values: WebToCaseFormValues,
  source?: SupportMessageSurface | SupportMessageSource,
): SupportCaseInput => ({
  contactName: values.contactName,
  email: values.email,
  categoryLevel1: values.categoryLevel1,
  categoryLevel2: getActiveCategoryLevel2(values),
  orderNumbers: isFieldVisible('orderNumbers', values) ? values.orderNumbers || null : null,
  description: values.description,
  source,
});

export const parseSupportMessageSource = (
  query: string | string[] | undefined,
): SupportMessageSource | undefined => SUPPORT_MESSAGE_SOURCES.find(source => source === query);

export const getStepFormikKeys = (step: 1 | 2): (keyof WebToCaseFormValues)[] =>
  webToCaseFields
    .filter(field => field.step === step)
    .map(field => (field.id === 'categoryLevel2' ? 'categoryLevel2ByCategory1' : field.id));

export const stepForFieldErrors = (
  fieldErrors: Partial<Record<FieldId, string>>,
): StepEnum | null => {
  const erroredIds = Object.keys(fieldErrors);
  const steps = webToCaseFields
    .filter(field => erroredIds.includes(field.id))
    .map(field => field.step);

  if (steps.length === 0) return null;

  return steps.includes(1) ? StepEnum.ContactAndCategory : StepEnum.CategoryAndMessage;
};

export const toFormikErrors = (
  fieldErrors: Partial<Record<FieldId, string>>,
  values: WebToCaseFormValues,
): FormikErrors<WebToCaseFormValues> => {
  const {categoryLevel2, ...flatErrors} = fieldErrors;
  const errors: FormikErrors<WebToCaseFormValues> = {...flatErrors};

  if (categoryLevel2 != null && values.categoryLevel1 !== '') {
    errors.categoryLevel2ByCategory1 = {[values.categoryLevel1]: categoryLevel2};
  }

  return errors;
};

export const getSubmitErrorKey = (reason?: string): string =>
  reason === 'rate_limited'
    ? 'components.SupportMessage.errors.rateLimited'
    : 'components.SupportMessage.errors.generic';

export const getInitialValues = (ctx: FieldContext): WebToCaseFormValues => {
  const defaultFor = (id: FieldId) =>
    webToCaseFields.find(f => f.id === id)?.defaultValue?.(ctx) ?? '';

  return {
    contactName: defaultFor('contactName'),
    email: defaultFor('email'),
    categoryLevel1: '',
    // Seeded with an empty slot per Category 1 rather than `{}`. Formik's submit-time touched pass
    // (`setNestedObjectValues(values)`) can only mark keys that already exist, so an absent slot
    // never becomes touched and SelectField would hide its required error — making Send look inert.
    // Empty string still reads as "unselected" everywhere (validation, retention, submit).
    categoryLevel2ByCategory1: Object.fromEntries(
      CATEGORY_LEVEL_1.map(({value}) => [value, '']),
    ) as WebToCaseFormValues['categoryLevel2ByCategory1'],
    orderNumbers: '',
    description: '',
  };
};
